import { useState, useEffect } from 'react'
import { Cloud, CheckCircle, XCircle, Trash2, Loader2, Link2 } from 'lucide-react'
import { useSearchParams } from 'react-router-dom'
import { api } from '../lib/api'
import toast from 'react-hot-toast'

interface OneDriveCredentialsPanelProps {
  adminConfigured: boolean
}

export default function OneDriveCredentialsPanel({ adminConfigured }: OneDriveCredentialsPanelProps) {
  const [searchParams, setSearchParams] = useSearchParams()
  const [loading, setLoading] = useState(true)
  const [connecting, setConnecting] = useState(false)
  const [connected, setConnected] = useState(false)
  const [accountEmail, setAccountEmail] = useState<string | null>(null)
  const [testStatus, setTestStatus] = useState<string | null>(null)
  const [accountModeDescription, setAccountModeDescription] = useState<string | null>(null)
  const [allowPersonalAccounts, setAllowPersonalAccounts] = useState<boolean | null>(null)

  const load = async () => {
    setLoading(true)
    try {
      const res = await api.get('/onedrive/status')
      const data = res.data?.data
      setConnected(!!data?.userConfigured)
      setAccountEmail(data?.accountEmail || null)
      setTestStatus(data?.testStatus || null)
      setAccountModeDescription(data?.accountModeDescription || null)
      setAllowPersonalAccounts(data?.allowPersonalAccounts ?? null)
    } catch {
      toast.error('OneDrive-Status konnte nicht geladen werden')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
  }, [])

  useEffect(() => {
    const od = searchParams.get('onedrive')
    const msg = searchParams.get('message')
    if (od === 'success') {
      toast.success('OneDrive erfolgreich verbunden')
      searchParams.delete('onedrive')
      searchParams.delete('message')
      setSearchParams(searchParams, { replace: true })
      load()
    } else if (od === 'error') {
      toast.error(msg ? decodeURIComponent(msg) : 'OneDrive-Verbindung fehlgeschlagen')
      searchParams.delete('onedrive')
      searchParams.delete('message')
      setSearchParams(searchParams, { replace: true })
    }
  }, [searchParams, setSearchParams])

  const handleConnect = async () => {
    if (!adminConfigured) {
      toast.error('OneDrive ist noch nicht durch die Administration eingerichtet')
      return
    }
    setConnecting(true)
    try {
      const returnUrl = `${window.location.origin}/settings?tab=personal`
      const res = await api.get('/onedrive/auth/url', { params: { returnUrl } })
      const url = res.data?.data?.url
      if (!url) throw new Error('Keine Anmelde-URL erhalten')
      window.location.href = url
    } catch (err: unknown) {
      const e = err as { response?: { data?: { error?: string } } }
      toast.error(e.response?.data?.error || 'Verbindung konnte nicht gestartet werden')
      setConnecting(false)
    }
  }

  const handleDisconnect = async () => {
    if (!confirm('OneDrive-Verbindung wirklich trennen?')) return
    try {
      await api.delete('/onedrive/connection')
      toast.success('OneDrive getrennt')
      setConnected(false)
      setAccountEmail(null)
      setTestStatus(null)
    } catch {
      toast.error('Trennen fehlgeschlagen')
    }
  }

  if (loading) {
    return (
      <div className="text-center py-6">
        <Loader2 className="h-6 w-6 animate-spin mx-auto text-gray-400" />
        <p className="mt-2 text-sm text-gray-500">Lade OneDrive-Einstellungen…</p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {!adminConfigured && (
        <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-3 dark:bg-amber-950/40 dark:border-amber-800 dark:text-amber-300">
          OneDrive wurde noch nicht durch die Administration eingerichtet (Azure App Registration). Bitte wenden Sie sich an Ihre IT-Verwaltung.
        </p>
      )}

      {connected && accountEmail && (
        <p className="text-sm text-gray-700 dark:text-gray-300">
          Verbunden als <strong>{accountEmail}</strong>
        </p>
      )}

      {testStatus && (
        <div className={`flex items-center text-sm ${
          testStatus === 'success' ? 'text-green-600' : testStatus === 'failed' ? 'text-red-600' : 'text-gray-500'
        }`}>
          {testStatus === 'success' && <CheckCircle className="w-4 h-4 mr-1" />}
          {testStatus === 'failed' && <XCircle className="w-4 h-4 mr-1" />}
          {testStatus === 'success' && 'Verbindung aktiv'}
          {testStatus === 'failed' && 'Letzte Verbindung fehlgeschlagen'}
        </div>
      )}

      {accountModeDescription && (
        <p className="text-sm text-sky-800 bg-sky-50 border border-sky-200 rounded-md p-3 dark:bg-sky-950/40 dark:border-sky-800 dark:text-sky-200">
          {accountModeDescription}
        </p>
      )}

      <p className="text-sm text-gray-500">
        {allowPersonalAccounts === false
          ? 'Melden Sie sich mit Ihrem Microsoft-Schul- oder Arbeitskonto an. Private @outlook.com-Konten sind von der Schule nicht erlaubt.'
          : 'Melden Sie sich mit Ihrem Microsoft-Schul- oder Privatkonto an. Es werden nur Dateizugriff und Profil abgefragt.'}
      </p>

      <div className="flex flex-wrap items-center gap-2">
        {!connected ? (
          <button
            type="button"
            onClick={handleConnect}
            disabled={!adminConfigured || connecting}
            className="inline-flex items-center gap-2 px-4 py-2 bg-[#0078d4] text-white text-sm font-medium rounded-md hover:bg-[#106ebe] disabled:opacity-50"
          >
            {connecting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Link2 className="h-4 w-4" />}
            Mit Microsoft verbinden
          </button>
        ) : (
          <button
            type="button"
            onClick={handleDisconnect}
            className="inline-flex items-center gap-2 px-4 py-2 text-red-600 text-sm font-medium rounded-md hover:bg-red-50"
          >
            <Trash2 className="h-4 w-4" />
            Verbindung trennen
          </button>
        )}
      </div>
    </div>
  )
}
